import type { JSX, ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { AlertCircle, ChevronDown, ChevronUp, Inbox, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../Button/Button';
import { Skeleton } from '../Skeleton/Skeleton';

import styles from './Table2.module.css';

export type TableDensity = 'comfortable' | 'compact' | 'dense';
export type TableRowState = 'default' | 'selected' | 'attention';
export type TableCellAlign = 'left' | 'right' | 'center';
export type ColumnPriority = 'high' | 'medium' | 'low';

export type HeaderColumn = {
  label: string;
  priority?: ColumnPriority;
  align?: TableCellAlign;
  numeric?: boolean;
};

export type HeaderEntry = string | HeaderColumn;

function normalizeHeader(entry: HeaderEntry): HeaderColumn {
  return typeof entry === 'string' ? { label: entry } : entry;
}

interface Table2ContextValue {
  density: TableDensity;
  selectable: boolean;
}

const Table2Context = createContext<Table2ContextValue>({
  density: 'comfortable',
  selectable: false,
});

/**
 * Set by a `<TableRow href="...">` for its descendant `<TableCell>`s. When
 * present, each non-interactive cell wraps its content in a real router
 * `<Link>` so the row target supports the browser's native
 * "Open in new tab" (right-click, ⌘/Ctrl-click, middle-click) — while a plain
 * left-click still routes client-side.
 */
const TableRowContext = createContext<{ rowHref?: string }>({});

export type TableMode = 'rows' | 'ledger';

type Table2Props = React.ComponentProps<'table'> & {
  density?: TableDensity;
  selectable?: boolean;
  stickyHeader?: boolean;
  /**
   * `rows` (default) renders the standard desktop-only table; the consumer pairs
   * it with a `<TableMobileCardList>` for mobile.
   * `ledger` is the escape hatch for number-heavy tables (invoices,
   * transactions) — visible at every width with the first column pinned via
   * horizontal scroll. No mobile card view is rendered in this mode.
   */
  mode?: TableMode;
};

type TableShellProps = React.ComponentProps<'div'>;

/**
 * Outer wrapper for every Table2 instance. Renders the consistent card surface
 * (white bg, 1px border, rounded corners) so pages don't redefine it. Holds the
 * `<TableTitleBar>`, the `<Table2>` itself, and any footer (pagination / load-more).
 */
function TableShell({ className, children, ...props }: TableShellProps): JSX.Element {
  return (
    <div className={classNames(styles.Shell, className)} {...props} data-rv-surface="">
      {children}
    </div>
  );
}

interface TableTitleBarProps {
  title: ReactNode;
  count?: number | string;
  children?: ReactNode;
  className?: string;
}

/**
 * Title row at the top of a `<TableShell>`. Renders the title with an optional
 * neutral count pill on the left, and any actions (filter, export, primary
 * button) on the right via `children`.
 */
function TableTitleBar({ title, count, children, className }: TableTitleBarProps): JSX.Element {
  return (
    <div className={classNames(styles.TitleBar, className)}>
      <h2 className={styles.Title}>
        {title}
        {count !== undefined && <span className={styles.TitleCount}>{count}</span>}
      </h2>
      {children && <div className={styles.TitleActions}>{children}</div>}
    </div>
  );
}

interface TableFiltersRowProps {
  children: ReactNode;
  className?: string;
}

/**
 * Container for search and filter controls inside a `<TableShell>`. Sits above
 * `<TableResultBar>` so filter UI shares the table card's border and background,
 * preventing visual collapse when the shell follows a tab strip or KPI grid.
 */
function TableFiltersRow({ children, className }: TableFiltersRowProps): JSX.Element {
  return <div className={classNames(styles.FiltersRow, className)}>{children}</div>;
}

interface TableResultBarProps {
  resultLine: ReactNode;
  children?: ReactNode;
  className?: string;
}

/**
 * Slim informational bar for tables that sit below a tab strip. Replaces the
 * redundant "Table name (N)" title with a result-line that carries real context
 * ("N invoices · X overdue · $Y outstanding") and optional utility actions.
 */
function TableResultBar({ resultLine, children, className }: TableResultBarProps): JSX.Element {
  return (
    <div className={classNames(styles.ResultBar, className)}>
      <div className={styles.ResultLine}>{resultLine}</div>
      {children && <div className={styles.ResultActions}>{children}</div>}
    </div>
  );
}

function Table2({
  children,
  density = 'comfortable',
  selectable = false,
  stickyHeader = false,
  mode = 'rows',
  className,
  ...props
}: Table2Props): JSX.Element {
  const ctx = useMemo(() => ({ density, selectable }), [density, selectable]);

  return (
    <Table2Context.Provider value={ctx}>
      <div
        className={styles.DesktopTable}
        data-sticky-header={stickyHeader || undefined}
        data-mode={mode}
      >
        <table
          className={classNames(styles.Table, className)}
          data-density={density}
          data-mode={mode}
          {...props}
        >
          {children}
        </table>
      </div>
    </Table2Context.Provider>
  );
}

interface IndeterminateCheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  indeterminate?: boolean;
  label?: string;
}

function IndeterminateCheckbox({
  indeterminate,
  label,
  className,
  ...props
}: IndeterminateCheckboxProps): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = !!indeterminate && !props.checked;
    }
  }, [indeterminate, props.checked]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className={classNames(styles.Checkbox, className)}
      aria-label={label}
      {...props}
    />
  );
}

interface SelectableHeaderProps {
  selectable?: boolean;
  selectAllChecked?: boolean;
  selectAllIndeterminate?: boolean;
  onSelectAllChange?: (checked: boolean) => void;
}

type TableHeaderRowProps = React.ComponentProps<'tr'> &
  SelectableHeaderProps & {
    headers: HeaderEntry[];
  };

function TableHeaderRow({
  className,
  headers,
  selectable: selectableProp,
  selectAllChecked,
  selectAllIndeterminate,
  onSelectAllChange,
  ...props
}: TableHeaderRowProps): JSX.Element {
  const ctx = useContext(Table2Context);
  const showSelection = selectableProp ?? ctx.selectable;

  return (
    <thead>
      <tr className={classNames(styles.TableHeaderRow, className)} {...props}>
        {showSelection && (
          <th className={classNames(styles.TableHeaderCell, styles.SelectionCell)}>
            <IndeterminateCheckbox
              checked={!!selectAllChecked}
              indeterminate={selectAllIndeterminate}
              onChange={(e) => onSelectAllChange?.(e.target.checked)}
              label="Select all rows"
            />
          </th>
        )}
        {headers.map((entry) => {
          const col = normalizeHeader(entry);
          const align = col.numeric ? 'right' : col.align;
          return (
            <th
              className={styles.TableHeaderCell}
              key={col.label}
              data-align={align}
              data-priority={col.priority}
              data-numeric={col.numeric || undefined}
            >
              {col.label}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

export interface SortableColumn {
  label: string;
  sortKey?: string;
  align?: TableCellAlign;
  numeric?: boolean;
  priority?: ColumnPriority;
  /**
   * Rendered inside the header cell after the label — e.g. a column resize
   * handle. It must stop its own click/key events from reaching the header,
   * which sorts on click.
   */
  headerAddon?: ReactNode;
}

export type SortDirection = 'ASC' | 'DESC';

type SortableTableHeaderRowProps = React.ComponentProps<'tr'> &
  SelectableHeaderProps & {
    columns: SortableColumn[];
    sortBy?: string;
    sortDirection?: SortDirection;
    onSort?: (sortKey: string) => void;
  };

function SortableTableHeaderRow({
  className,
  columns,
  sortBy,
  sortDirection,
  onSort,
  selectable: selectableProp,
  selectAllChecked,
  selectAllIndeterminate,
  onSelectAllChange,
  ...props
}: SortableTableHeaderRowProps): JSX.Element {
  const ctx = useContext(Table2Context);
  const showSelection = selectableProp ?? ctx.selectable;

  const handleColumnClick = (sortKey: string | undefined) => {
    if (sortKey && onSort) {
      onSort(sortKey);
    }
  };

  return (
    <thead>
      <tr className={classNames(styles.TableHeaderRow, className)} {...props}>
        {showSelection && (
          <th className={classNames(styles.TableHeaderCell, styles.SelectionCell)}>
            <IndeterminateCheckbox
              checked={!!selectAllChecked}
              indeterminate={selectAllIndeterminate}
              onChange={(e) => onSelectAllChange?.(e.target.checked)}
              label="Select all rows"
            />
          </th>
        )}
        {columns.map((column, index) => {
          const isSortable = !!column.sortKey;
          const isActive = column.sortKey === sortBy;
          const align = column.numeric ? 'right' : (column.align ?? 'left');

          return (
            <th
              key={column.label || index}
              className={classNames(styles.TableHeaderCell, {
                [styles.sortable]: isSortable,
                [styles.sortActive]: isActive,
              })}
              data-align={align}
              data-numeric={column.numeric || undefined}
              data-priority={column.priority}
              onClick={() => handleColumnClick(column.sortKey)}
              role={isSortable ? 'button' : undefined}
              tabIndex={isSortable ? 0 : undefined}
              onKeyDown={(e) => {
                if (isSortable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleColumnClick(column.sortKey);
                }
              }}
            >
              <span className={styles.headerContent}>
                <span className={styles.headerLabel}>{column.label}</span>
                {isSortable && (
                  <span className={styles.sortIconWrapper}>
                    {isActive ? (
                      sortDirection === 'DESC' ? (
                        <ChevronDown className={styles.sortIcon} />
                      ) : (
                        <ChevronUp className={styles.sortIcon} />
                      )
                    ) : (
                      <ChevronDown
                        className={classNames(styles.sortIcon, styles.sortIconInactive)}
                      />
                    )}
                  </span>
                )}
              </span>
              {column.headerAddon}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}

function TableBody({ className, children, ...props }: React.ComponentProps<'tbody'>): JSX.Element {
  return (
    <tbody className={classNames(styles.TableBody, className)} {...props}>
      {children}
    </tbody>
  );
}

type TableRowProps = React.ComponentProps<'tr'> & {
  state?: TableRowState;
  selectable?: boolean;
  selected?: boolean;
  onSelectedChange?: (checked: boolean) => void;
  selectionLabel?: string;
  /**
   * Client-side route the row points to. When set, each non-interactive cell
   * renders its content inside a router `<Link>`, so the row supports the
   * browser's native "Open in new tab" (right-click, ⌘/Ctrl-click,
   * middle-click). A plain left-click anywhere on the row still navigates
   * client-side. Prefer this over an `onClick` that calls `navigate(...)`.
   */
  href?: string;
  /**
   * Keep the selection cell (so columns stay aligned with the header) but make
   * the checkbox inert. For a row that is legitimately in the list yet must not
   * be swept into a bulk action.
   */
  selectionDisabled?: boolean;
};

function TableRow({
  className,
  children,
  state,
  selectable: selectableProp,
  selected,
  onSelectedChange,
  selectionLabel,
  selectionDisabled,
  href,
  onClick,
  ...props
}: TableRowProps): JSX.Element {
  const ctx = useContext(Table2Context);
  const navigate = useNavigate();
  const showSelection = selectableProp ?? ctx.selectable;
  const resolvedState: TableRowState | undefined = state ?? (selected ? 'selected' : undefined);

  const handleClick =
    href || onClick
      ? (e: React.MouseEvent<HTMLTableRowElement>) => {
          onClick?.(e);
          if (!href) return;
          // A cell's <Link> already handled this (calls preventDefault on a
          // plain left-click), or the user asked for a new tab / used a
          // non-primary button — let the browser's native behavior stand.
          if (
            e.defaultPrevented ||
            e.button !== 0 ||
            e.metaKey ||
            e.ctrlKey ||
            e.shiftKey ||
            e.altKey
          ) {
            return;
          }
          navigate(href);
        }
      : undefined;

  return (
    <tr
      className={classNames(styles.TableRow, className)}
      data-state={resolvedState && resolvedState !== 'default' ? resolvedState : undefined}
      data-selectable={showSelection || undefined}
      onClick={handleClick}
      {...props}
    >
      {showSelection && (
        <td
          className={classNames(styles.TableCell, styles.SelectionCell)}
          onClick={(e) => e.stopPropagation()}
        >
          <IndeterminateCheckbox
            checked={!!selected}
            disabled={selectionDisabled}
            onChange={(e) => onSelectedChange?.(e.target.checked)}
            label={selectionLabel ?? 'Select row'}
          />
        </td>
      )}
      <TableRowContext.Provider value={href ? { rowHref: href } : EMPTY_ROW_CONTEXT}>
        {children}
      </TableRowContext.Provider>
    </tr>
  );
}

const EMPTY_ROW_CONTEXT: { rowHref?: string } = {};

type TableCellProps = React.ComponentProps<'td'> & {
  align?: TableCellAlign;
  numeric?: boolean;
  priority?: ColumnPriority;
  /**
   * Opt this cell out of the row `<Link>` wrapping (see `TableRow.href`). Set
   * automatically for cells that carry their own `onClick` (e.g. an actions
   * cell that stops propagation), so its interactive content isn't nested
   * inside an anchor. Pass explicitly for other interactive cells.
   */
  noLink?: boolean;
};

function TableCell({
  className,
  children,
  align,
  numeric,
  priority,
  noLink,
  ...props
}: TableCellProps): JSX.Element {
  const { rowHref } = useContext(TableRowContext);
  const resolvedAlign: TableCellAlign | undefined = align ?? (numeric ? 'right' : undefined);
  // Interactive cells (their own onClick) hold buttons/menus that must not be
  // nested inside an anchor, so they never get the row link.
  const linked = !!rowHref && !noLink && !props.onClick;

  return (
    <td
      className={classNames(styles.TableCell, className)}
      data-align={resolvedAlign}
      data-numeric={numeric || undefined}
      data-priority={priority}
      {...props}
    >
      {linked ? (
        <Link to={rowHref} className={styles.CellLink} tabIndex={-1}>
          {children}
        </Link>
      ) : (
        children
      )}
    </td>
  );
}

interface TableToolbarProps {
  selectionCount: number;
  itemNoun?: string;
  onClear?: () => void;
  children?: ReactNode;
}

function TableToolbar({
  selectionCount,
  itemNoun = 'selected',
  onClear,
  children,
}: TableToolbarProps): JSX.Element | null {
  if (selectionCount <= 0) return null;

  return (
    <div className={styles.Toolbar} role="toolbar" aria-label={`${selectionCount} ${itemNoun}`}>
      <span className={styles.ToolbarCount}>
        <span className={styles.ToolbarCountValue}>{selectionCount}</span>
        <span className={styles.ToolbarCountLabel}>{itemNoun}</span>
      </span>
      {children && <div className={styles.ToolbarActions}>{children}</div>}
      {onClear && (
        <button
          type="button"
          className={styles.ToolbarClear}
          onClick={onClear}
          aria-label="Clear selection"
        >
          <X className={styles.ToolbarClearIcon} aria-hidden="true" />
          Clear selection
        </button>
      )}
    </div>
  );
}

export type TableEmptyStateVariant = 'loading' | 'empty' | 'filtered' | 'error';

interface TableEmptyStateProps {
  variant: TableEmptyStateVariant;
  title?: string;
  description?: string;
  icon?: ReactNode;
  primaryAction?: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
  skeletonRows?: number;
  skeletonColumns?: number;
}

function TableEmptyState({
  variant,
  title,
  description,
  icon,
  primaryAction,
  secondaryAction,
  skeletonRows = 5,
  skeletonColumns = 5,
}: TableEmptyStateProps): JSX.Element {
  if (variant === 'loading') {
    return (
      <div className={styles.SkeletonContainer} aria-busy="true" aria-live="polite">
        {Array.from({ length: skeletonRows }).map((_, rowIdx) => (
          <div className={styles.SkeletonRow} key={rowIdx}>
            {Array.from({ length: skeletonColumns }).map((__, colIdx) => (
              <Skeleton
                key={colIdx}
                className={classNames(styles.SkeletonCell, {
                  [styles.SkeletonCellNarrow]: colIdx === 0,
                })}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  const defaultIcon =
    variant === 'error' ? <AlertCircle aria-hidden="true" /> : <Inbox aria-hidden="true" />;

  return (
    <div className={styles.EmptyState} data-variant={variant} role="status">
      <div className={styles.EmptyStateIcon}>{icon ?? defaultIcon}</div>
      {title && <h3 className={styles.EmptyStateTitle}>{title}</h3>}
      {description && <p className={styles.EmptyStateDescription}>{description}</p>}
      {(primaryAction || secondaryAction) && (
        <div className={styles.EmptyStateActions}>
          {secondaryAction && (
            <Button variant="outline" size="sm" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
          {primaryAction && (
            <Button variant="primary" size="sm" onClick={primaryAction.onClick}>
              {primaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

type TableMobileCardListProps = React.ComponentProps<'div'>;

function TableMobileCardList({
  className,
  children,
  ...props
}: TableMobileCardListProps): JSX.Element {
  return (
    <div className={classNames(styles.MobileCardList, className)} {...props}>
      {children}
    </div>
  );
}

type TableMobileSectionProps = React.ComponentProps<'section'> & {
  title: string;
};

function TableMobileSection({
  className,
  title,
  children,
  ...props
}: TableMobileSectionProps): JSX.Element {
  return (
    <section className={classNames(styles.MobileSection, className)} {...props}>
      <h3 className={styles.MobileSectionTitle}>{title}</h3>
      {children}
    </section>
  );
}

/**
 * Pattern B helpers — long-press detection. Fires `callback` after `ms` of
 * sustained pointer-down without significant movement. Pointer move beyond
 * `threshold` cancels the timer (so a swipe doesn't trigger a long-press).
 *
 * Returns handlers to spread on the target plus `wasTriggered()` so the
 * consumer can suppress a follow-up click event.
 */
function useLongPress(
  callback: (() => void) | undefined,
  options: { ms?: number; threshold?: number } = {},
): {
  handlers: Pick<
    React.HTMLAttributes<HTMLElement>,
    'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel' | 'onPointerLeave'
  >;
  wasTriggered: () => boolean;
} {
  const { ms = 500, threshold = 8 } = options;
  const timerRef = useRef<number | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const triggeredRef = useRef(false);

  const clear = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  return {
    handlers: {
      onPointerDown: (e) => {
        if (!callback) return;
        triggeredRef.current = false;
        startRef.current = { x: e.clientX, y: e.clientY };
        clear();
        timerRef.current = window.setTimeout(() => {
          triggeredRef.current = true;
          callback();
        }, ms);
      },
      onPointerMove: (e) => {
        if (!startRef.current) return;
        const dx = e.clientX - startRef.current.x;
        const dy = e.clientY - startRef.current.y;
        if (Math.abs(dx) > threshold || Math.abs(dy) > threshold) {
          clear();
        }
      },
      onPointerUp: () => {
        clear();
        startRef.current = null;
      },
      onPointerCancel: () => {
        clear();
        startRef.current = null;
      },
      onPointerLeave: () => {
        clear();
        startRef.current = null;
      },
    },
    wasTriggered: () => triggeredRef.current,
  };
}

export type SwipeAction = {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'destructive';
};

const SWIPE_THRESHOLD = 80;
const SWIPE_ACTION_WIDTH = 80;

type TableMobileCardProps = Omit<React.ComponentProps<'div'>, 'title'> & {
  avatar?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  primary?: ReactNode;
  secondary?: ReactNode;
  state?: TableRowState;
  onClick?: () => void;
  /** Pattern B — long-press the card to enter a select-mode flow. */
  onLongPress?: () => void;
  /** Pattern B — when true, the card shows a checkbox and tap toggles
   *  selection instead of firing onClick. */
  selectMode?: boolean;
  selected?: boolean;
  onSelectedChange?: (checked: boolean) => void;
  /** Pattern B — destructive row actions revealed by a left-swipe. */
  swipeActions?: SwipeAction[];
};

function TableMobileCard({
  avatar,
  title,
  subtitle,
  primary,
  secondary,
  state,
  onClick,
  onLongPress,
  selectMode,
  selected,
  onSelectedChange,
  swipeActions,
  className,
  ...props
}: TableMobileCardProps): JSX.Element {
  const longPress = useLongPress(onLongPress);

  // Swipe-to-reveal — only active outside select mode and when actions exist.
  const swipeEnabled = !!swipeActions && swipeActions.length > 0 && !selectMode;
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeOpen, setSwipeOpen] = useState(false);
  const swipeStartRef = useRef<{ x: number; offset: number } | null>(null);
  const swipeDraggingRef = useRef(false);
  const totalActionsWidth = swipeActions
    ? Math.min(swipeActions.length, 2) * SWIPE_ACTION_WIDTH
    : 0;

  const closeSwipe = () => {
    swipeStartRef.current = null;
    swipeDraggingRef.current = false;
    setSwipeOffset(0);
    setSwipeOpen(false);
  };

  const handleSwipeAction = (action: SwipeAction) => {
    closeSwipe();
    action.onClick();
  };

  const resolvedState = selectMode && selected ? 'selected' : state;

  const interactive = !!(onClick || (selectMode && onSelectedChange) || onLongPress);

  const handleClick = () => {
    if (longPress.wasTriggered()) return;
    if (swipeOpen) {
      closeSwipe();
      return;
    }
    if (selectMode && onSelectedChange) {
      onSelectedChange(!selected);
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <div className={styles.MobileCardSwipeRoot} data-swipe-open={swipeOpen || undefined}>
      {swipeEnabled && (
        <div className={styles.MobileCardSwipeActions} aria-hidden={!swipeOpen}>
          {swipeActions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={styles.MobileCardSwipeAction}
              data-variant={action.variant ?? 'default'}
              onClick={(e) => {
                e.stopPropagation();
                handleSwipeAction(action);
              }}
              tabIndex={swipeOpen ? 0 : -1}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
      <div
        className={classNames(styles.MobileCard, className)}
        style={swipeEnabled ? { transform: `translateX(${swipeOffset}px)` } : undefined}
        data-state={resolvedState && resolvedState !== 'default' ? resolvedState : undefined}
        data-interactive={interactive || undefined}
        data-select-mode={selectMode || undefined}
        data-swiping={swipeDraggingRef.current || undefined}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        onClick={handleClick}
        onKeyDown={
          interactive
            ? (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              }
            : undefined
        }
        onPointerDown={(e) => {
          longPress.handlers.onPointerDown?.(e);
          if (swipeEnabled) {
            swipeStartRef.current = { x: e.clientX, offset: swipeOpen ? -totalActionsWidth : 0 };
            swipeDraggingRef.current = false;
          }
        }}
        onPointerMove={(e) => {
          longPress.handlers.onPointerMove?.(e);
          if (!swipeEnabled || !swipeStartRef.current) return;
          const dx = e.clientX - swipeStartRef.current.x;
          if (!swipeDraggingRef.current && Math.abs(dx) < 4) return;
          swipeDraggingRef.current = true;
          const next = swipeStartRef.current.offset + dx;
          setSwipeOffset(Math.max(Math.min(next, 0), -totalActionsWidth));
        }}
        onPointerUp={(e) => {
          longPress.handlers.onPointerUp?.(e);
          if (!swipeEnabled || !swipeDraggingRef.current) {
            swipeStartRef.current = null;
            return;
          }
          swipeStartRef.current = null;
          swipeDraggingRef.current = false;
          const open = swipeOffset < -SWIPE_THRESHOLD;
          setSwipeOffset(open ? -totalActionsWidth : 0);
          setSwipeOpen(open);
        }}
        onPointerCancel={(e) => {
          longPress.handlers.onPointerCancel?.(e);
          closeSwipe();
        }}
        onPointerLeave={longPress.handlers.onPointerLeave}
        {...props}
      >
        {selectMode ? (
          <div
            className={styles.MobileCardSelector}
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              className={styles.Checkbox}
              checked={!!selected}
              onChange={(e) => onSelectedChange?.(e.target.checked)}
              aria-label="Toggle selection"
            />
          </div>
        ) : (
          avatar && <div className={styles.MobileCardAvatar}>{avatar}</div>
        )}
        <div className={styles.MobileCardIdentity}>
          <div className={styles.MobileCardTitle}>{title}</div>
          {subtitle && <div className={styles.MobileCardSubtitle}>{subtitle}</div>}
        </div>
        {(primary || secondary) && (
          <div className={styles.MobileCardSummary}>
            {primary && <div className={styles.MobileCardPrimary}>{primary}</div>}
            {secondary && <div className={styles.MobileCardSecondary}>{secondary}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Pattern B — replaces the page header on mobile while a select session is
 * active. Renders the count, a Cancel exit, and an optional Select-all toggle.
 */
type TableMobileSelectHeaderProps = {
  selectionCount: number;
  totalCount?: number;
  itemNoun?: string;
  onCancel: () => void;
  onSelectAll?: () => void;
  allSelected?: boolean;
};

function TableMobileSelectHeader({
  selectionCount,
  totalCount,
  itemNoun = 'selected',
  onCancel,
  onSelectAll,
  allSelected,
}: TableMobileSelectHeaderProps): JSX.Element {
  return (
    <header className={styles.MobileSelectHeader} role="toolbar">
      <button type="button" className={styles.MobileSelectHeaderButton} onClick={onCancel}>
        Cancel
      </button>
      <span className={styles.MobileSelectHeaderCount}>
        {selectionCount} {itemNoun}
      </span>
      {onSelectAll ? (
        <button
          type="button"
          className={styles.MobileSelectHeaderButton}
          onClick={onSelectAll}
          aria-pressed={allSelected}
        >
          {allSelected ? 'Clear all' : `Select all${totalCount ? ` (${totalCount})` : ''}`}
        </button>
      ) : (
        <span className={styles.MobileSelectHeaderSpacer} />
      )}
    </header>
  );
}

/**
 * Pattern B — bottom-pinned bulk-action bar that appears while select mode is
 * active. Mirrors the desktop `TableToolbar` actions but optimized for thumbs.
 */
type TableMobileActionBarProps = {
  visible: boolean;
  children?: ReactNode;
};

function TableMobileActionBar({
  visible,
  children,
}: TableMobileActionBarProps): JSX.Element | null {
  if (!visible) return null;
  return (
    <div className={styles.MobileActionBar} role="toolbar">
      <div className={styles.MobileActionBarInner}>{children}</div>
    </div>
  );
}

export {
  SortableTableHeaderRow,
  Table2,
  TableBody,
  TableCell,
  TableEmptyState,
  TableFiltersRow,
  TableHeaderRow,
  TableMobileActionBar,
  TableMobileCard,
  TableMobileCardList,
  TableMobileSection,
  TableMobileSelectHeader,
  TableResultBar,
  TableRow,
  TableShell,
  TableTitleBar,
  TableToolbar,
};
